import { useState, useEffect, useMemo } from 'react';
import debounce from 'lodash.debounce';

import { DesktopLayout } from '../../components/Layouts';
import { FundsOverview } from '../../components/FundsOverview';
import { Pagination } from '../../components/Pagination';
import { Tabs } from '../../components/Tabs';
import { SearchActions } from '../../components/SearchActions';
import { SalesTable, WithdrawTable } from '../../components/Table';
import useAllProjects from '../../hooks/useAllProjects';
import useFantomStarterAdminApi from '../../hooks/useFantomStarterAdminApi';
import useToken from '../../hooks/useToken';
import { formatWithdrawDate } from '../../utils/conversion';
import { g8wyLogo } from '../../assets';

export interface ISaleDetailItem {
  label: string;
  value: string;
}

export interface ISaleTableData {
  customer: string;
  saleId: string;
  saleType: string;
  status: string;
  saleDate: string;
  saleFees: string;
  saleAmount: string;
  detailItems: ISaleDetailItem[];
}

export interface IWithdrawTableData {
  transaction: string;
  transactionId: string;
  transactionType: string;
  transactionDate: string;
  fee: string;
  amount: string;
  status: string;
}

export default function Financials() {
  const { projects } = useAllProjects();
  const {
    circle: { getWithdrawsForSeller },
  } = useFantomStarterAdminApi();
  const { token } = useToken();

  const [filteredProjects, setFilteredProjects] = useState<any[]>([]);
  const [filteredWithdraws, setFilteredWithdraws] = useState<IWithdrawTableData[]>([]);

  const [saleTableData, setSaleTableData] = useState<ISaleTableData[]>([]);
  const [withdrawTableData, setWithdrawTableData] = useState<
    IWithdrawTableData[]
  >([]);
  const [toogleDetail, setToogleDetail] = useState<boolean>(false);
  const [toggleDetailIndex, setToggleDetailIndex] = useState<number>(-1);
  const [tabs, setTabs] = useState([
    { label: 'Sales History', isActive: true },
    { label: 'Withdraw History', isActive: false },
  ]);
  const [searchQuery, setSearchQuery] = useState<string>('');

  const [popoverVisible, setPopoverVisible] = useState<boolean>(false);
  const [popoverTransactionId, setPopoverTransactionId] = useState<
    string | null
  >(null);

  const salesHeaders = [
    'Customer',
    'Sale ID',
    'Sale Type',
    'Status',
    'Sale Date',
    'Sale Fees',
    'Sale Amount',
  ];
  const withdrawHeaders = [
    'Transaction',
    'Transaction ID',
    'Transaction Type',
    'Transaction Date',
    'Fee',
    'Amount',
    'Status',
  ];

  const handleTabClick = (index: number) => {
    const updatedTabs = tabs.map((tab, i) => ({
      ...tab,
      isActive: i === index,
    }));
    setTabs(updatedTabs);
    setPopoverVisible(false);
    setPopoverTransactionId(null);
  };

  const handleDetailClick = (index: number) => {
    setToogleDetail(!toogleDetail || toggleDetailIndex !== index);
    setToggleDetailIndex(index);
  };

  const handleTransactionClick = (transactionId: string) => {
    setPopoverVisible(!popoverVisible);
    setPopoverTransactionId(popoverVisible ? null : transactionId);
  };

  const closePopover = () => {
    setPopoverVisible(false);
    setPopoverTransactionId(null);
  };

  const debouncedSearch = useMemo(
    () => debounce((query: string) => setSearchQuery(query), 300),
    []
  );

  useEffect(() => {
    let filtered = projects;

    if (searchQuery && tabs[0].isActive) {
      const lowercasedQuery = searchQuery.toLowerCase();
      filtered = filtered.filter(
        (project) =>
          project.name.toLowerCase().includes(lowercasedQuery) ||
          project.id.toLowerCase().includes(lowercasedQuery)
      );
    }

    setFilteredProjects(filtered);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchQuery, projects, tabs]);

  useEffect(() => {
    try {
      if (!Array.isArray(withdrawTableData)) {
        setFilteredWithdraws([]);
        return;
      }
      
      let filtered = [...withdrawTableData];

      if (searchQuery && tabs[1]?.isActive) {
        const lowercasedQuery = searchQuery.toLowerCase();
        filtered = filtered.filter(
          (withdraw) =>
            (withdraw?.transaction?.toLowerCase() || '').includes(lowercasedQuery) ||
            (withdraw?.transactionId?.toLowerCase() || '').includes(lowercasedQuery) ||
            (withdraw?.status?.toLowerCase() || '').includes(lowercasedQuery)
        );
      }

      setFilteredWithdraws(filtered);
    } catch (error) {
      console.error('Error filtering withdraw data:', error);
      setFilteredWithdraws([]);
    }
  }, [searchQuery, withdrawTableData, tabs]);

  useEffect(() => {
    return () => {
      debouncedSearch.cancel();
    };
  }, [debouncedSearch]);

  useEffect(() => {
    if (filteredProjects && Array.isArray(filteredProjects)) {
      try {
        const mappedSalesData = filteredProjects.map((project: any) => ({
          customer: 'Unknown',
          saleId: project?.project_id || 'Unknown',
          saleType: project?.with_lottery
            ? 'Auction'
            : project?.with_whitelist
              ? 'Registered'
              : 'Open',
          status: project?.total_raised > 0 ? 'Sent' : 'Pending',
          saleDate: project?.start_date_funding 
            ? new Date(project.start_date_funding).toLocaleDateString() 
            : 'Unknown',
          saleFees: project?.cap_individual_dollars 
            ? `$${parseFloat(project.cap_individual_dollars).toFixed(2)}` 
            : '$0.00',
          saleAmount: project?.cap_individual_dollars 
            ? `$${parseFloat(project.cap_individual_dollars).toFixed(2)}` 
            : '$0.00',
          detailItems: [
            { label: 'Sale Name', value: project?.name || 'Unknown' },
            {
              label: 'Fee Name',
              value: project?.cap_individual_dollars 
                ? `$${parseFloat(project.cap_individual_dollars).toFixed(2)}` 
                : '$0.00',
            },
            { label: 'Withdraw Date', value: 'N/A' },
          ],
        }));
        setSaleTableData(mappedSalesData);
      } catch (error) {
        console.error('Error mapping project data:', error);
        setSaleTableData([]);
      }
    } else {
      setSaleTableData([]);
    }
  }, [filteredProjects]);

  useEffect(() => {
    (async () => {
      if (token) {
        try {
          const result = await getWithdrawsForSeller();
          const sellerWithdraws = result?.data?.data || [];

          if (Array.isArray(sellerWithdraws)) {
            const mappedWithdrawData = sellerWithdraws.map((withdraw: any) => ({
              transaction: `Withdraw ${withdraw?.amount?.currency || 'Unknown'}`,
              transactionId: withdraw?.id ? withdraw.id.slice(-5) : 'Unknown',
              transactionType: 'Bank Account',
              transactionDate: withdraw?.createDate ? formatWithdrawDate(withdraw.createDate) : 'Unknown',
              fee: `$${withdraw?.fees?.amount ? withdraw.fees.amount : '0'}`,
              amount: `$${withdraw?.amount?.amount || '0'}`,
              status: withdraw?.status || 'Unknown',
            }));

            setWithdrawTableData(mappedWithdrawData);
          } else {
            console.warn('Withdraw data is not an array:', sellerWithdraws);
            setWithdrawTableData([]);
          }
        } catch (error) {
          console.error('Error fetching withdraw data:', error);
          setWithdrawTableData([]);
        }
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  return (
    <DesktopLayout>
      <main className="pt-20 sm:pt-24 px-4 sm:px-8 lg:px-16 xl:px-32 flex flex-col items-start">
        <div className="flex items-center mb-4 sm:mb-6 w-full">
          <div className="flex items-center">
            <img 
              src={g8wyLogo} 
              alt="Equitee" 
              className="h-8 sm:h-10 mr-2" 
            />
            <h1 className="text-xl sm:text-2xl font-bold text-[#333333]">Financials</h1>
          </div>
        </div>
        
        <FundsOverview />

        <div className="w-full mt-6">
          <Tabs tabs={tabs} onTabClick={handleTabClick} />
        </div>

        <div className="w-full mt-4">
          <SearchActions
            onSearchChange={(event: React.ChangeEvent<HTMLInputElement>) => {
              debouncedSearch(event.target.value);
            }}
            onFilterClick={() => {}}
            onExportClick={() => {}}
            onStartSaleClick={() => {}}
          />
        </div>

        <div className="w-full mt-4 overflow-x-auto">
          {tabs[0].isActive ? (
            <SalesTable
              salesHeaders={salesHeaders}
              saleTableData={saleTableData}
              onDetailClick={handleDetailClick}
              toggleDetailIndex={toggleDetailIndex}
              toogleDetail={toogleDetail}
            />
          ) : (
            <WithdrawTable
              withdrawHeaders={withdrawHeaders}
              withdrawTableData={filteredWithdraws}
              onTransactionClick={handleTransactionClick}
              popoverVisible={popoverVisible}
              popoverTransactionId={popoverTransactionId}
              closePopover={closePopover}
            />
          )}
        </div>

        <div className="w-full mt-4">
          <Pagination />
        </div>
      </main>
    </DesktopLayout>
  );
}
